import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

/** Mirrors Rails `Pagination::Collection`. */
export type SeatPaginationMeta = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

type SeatPaginationProps = {
  pagination: SeatPaginationMeta;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
};

export function SeatPagination({ pagination, isLoading = false, onPageChange }: SeatPaginationProps) {
  const { page, per_page, total, total_pages } = pagination;
  const canPrev = page > 1 && !isLoading;
  const canNext = page < total_pages && !isLoading;

  const firstRow = total === 0 ? 0 : (page - 1) * per_page + 1;
  const lastRow = Math.min(page * per_page, total);

  return (
    <div className="flex items-center justify-between text-xs text-gray-600">
      <span aria-live="polite">
        Showing {firstRow}–{lastRow} of {total}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="tabular-nums">
          Page {page} of {Math.max(total_pages, 1)}
        </span>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
